/**
 * Thin, named wrappers around posthog.capture().
 *
 * Keeping event names and property shapes in one place makes it easy to:
 *  - search for all callsites of a particular event
 *  - rename / deprecate events without grep-hunting through the whole app
 *  - add PostHog feature flag checks alongside the relevant user action
 *
 * All functions are safe to call when PostHog is not initialised (disabled in
 * dev or key missing) — posthog-js no-ops silently in that case.
 */

import posthog from './posthog';

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

/**
 * @param {'password'|'mfa'|'cognito'|'google'|'outlook'} method
 */
export const trackLoginCompleted = (method) =>
  posthog.capture('login_completed', { method });

export const trackLogout = () => posthog.capture('logout');

export const trackMfaStarted = () => posthog.capture('mfa_started');

export const trackPasswordResetStarted = () =>
  posthog.capture('password_reset_started');

export const trackLicenceAccepted = () => posthog.capture('licence_accepted');

// ---------------------------------------------------------------------------
// Jobs
// ---------------------------------------------------------------------------

/**
 * @param {{ jobId?: string|number, jobTitle?: string, companyId?: string|number }} props
 */
export const trackJobCreated = (props = {}) =>
  posthog.capture('job_created', {
    job_id: props.jobId,
    job_title: props.jobTitle,
    company_id: props.companyId,
  });

/**
 * @param {{ jobId?: string|number, fromStage?: string, toStage?: string, candidateId?: string|number }} props
 */
export const trackApplicationStageChanged = (props = {}) =>
  posthog.capture('application_stage_changed', {
    job_id: props.jobId,
    from_stage: props.fromStage,
    to_stage: props.toStage,
    candidate_id: props.candidateId,
  });

// ---------------------------------------------------------------------------
// Offers / agreements
// ---------------------------------------------------------------------------

/**
 * @param {{ jobId?: string|number, candidateId?: string|number }} props
 */
export const trackOfferSent = (props = {}) =>
  posthog.capture('offer_sent', {
    job_id: props.jobId,
    candidate_id: props.candidateId,
  });

// ---------------------------------------------------------------------------
// Settings / integrations
// ---------------------------------------------------------------------------

/**
 * @param {'google_calendar'|'outlook_calendar'|string} integration
 */
export const trackIntegrationConnected = (integration) =>
  posthog.capture('integration_connected', { integration });

// ---------------------------------------------------------------------------
// Feature flags — convenience helpers
// ---------------------------------------------------------------------------

/**
 * Returns true if the PostHog feature flag is enabled for the current user.
 * Falls back to `defaultValue` when PostHog is not loaded or the flag is
 * unknown, so callers can keep a safe default without try/catch.
 *
 * @param {string} flagKey
 * @param {boolean} [defaultValue=false]
 * @returns {boolean}
 */
export const isFeatureEnabled = (flagKey, defaultValue = false) => {
  try {
    return posthog.isFeatureEnabled(flagKey) ?? defaultValue;
  } catch {
    return defaultValue;
  }
};

/**
 * Returns the payload for a multivariate feature flag. Use when you need a
 * string variant (e.g. A/B copy test) rather than a boolean gate.
 *
 * @param {string} flagKey
 * @param {*} [defaultValue=undefined]
 */
export const getFeatureFlagPayload = (flagKey, defaultValue = undefined) => {
  try {
    return posthog.getFeatureFlagPayload(flagKey) ?? defaultValue; // NOSONAR
  } catch {
    return defaultValue;
  }
};
